import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import {
    type ToolRailGroup,
    useToolRailSlotRef,
} from '~/components/toolbar/shared/ToolRailSlots';
import { GRID_LEFT_EDGE_RADIUS_VAR } from '~/viewportGrid';

/** Width of the rail, its border included, in CSS pixels. */
const TOOL_RAIL_WIDTH_PX = 44;
/** Length of the rule between the groups, in CSS pixels. */
const GROUP_RULE_PX = 26;
/** Gap between the rail's buttons, in CSS pixels. */
const BUTTON_GAP = '4px';

/**
 * The slot of one group of the rail. It is `display: contents`, so the
 * buttons rendered into it are laid out by the rail itself.
 */
function ToolRailGroupSlot({ group }: { group: ToolRailGroup }) {
    const slotRef = useToolRailSlotRef(group);
    return (
        <Box
            ref={slotRef}
            sx={{
                display: 'contents',
                // A button's wrapping span would otherwise add a line's
                // descent below it.
                lineHeight: 0,
                // The operations arrive in their own column wrappers. The
                // element selector outranks the wrappers' own gap.
                '& > div': { gap: BUTTON_GAP },
            }}
        />
    );
}

/**
 * A vertical rail of toolbar buttons along the left edge of the viewport
 * grid, in two groups split by a rule, which shows only while the second
 * group holds buttons. Toolbars elsewhere in the layout render their
 * buttons into its slots (see `ToolRailSlotsProvider`), and the rail shows
 * only while they do. The buttons sit centered along its height, and it
 * scrolls on its own when it is shorter than they are.
 */
export default function ToolRail() {
    const intl = useIntl();
    return (
        <Box
            role="toolbar"
            aria-orientation="vertical"
            aria-label={intl.formatMessage({ id: 'toolRail.ariaLabel' })}
            sx={(theme) => ({
                width: TOOL_RAIL_WIDTH_PX,
                flexShrink: 0,
                boxSizing: 'border-box',
                // The rail joins the grid's left edge: framed on its other
                // three sides, rounded on the outer corners, and ending on
                // the grid's bottom line. The grid's left edge then runs
                // straight.
                border: `1px solid ${theme.palette.divider}`,
                borderRight: 0,
                borderRadius: '4px 0 0 4px',
                mb: 1,
                '&:has(button) + *': { [GRID_LEFT_EDGE_RADIUS_VAR]: '0px' },
                '&:not(:has(button))': { display: 'none' },
                bgcolor: 'background.raised',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                // Centered while the buttons fit. "safe" keeps the top
                // buttons reachable by scrolling once they do not.
                justifyContent: 'safe center',
                gap: BUTTON_GAP,
                py: 1.5,
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
            })}
        >
            <ToolRailGroupSlot group="tools" />
            <Box
                sx={{
                    width: GROUP_RULE_PX,
                    height: '1px',
                    flexShrink: 0,
                    my: '6px',
                    bgcolor: 'divider',
                    '&:has(+ :empty)': { display: 'none' },
                }}
            />
            <ToolRailGroupSlot group="ops" />
        </Box>
    );
}
